import React, { forwardRef, useCallback, useEffect } from 'react'
import { RefCallBack, useController, UseFormSetValue } from 'react-hook-form'
import { FormControl, RadioGroup, FormControlLabel, FormHelperText, Radio, styled } from '@mui/material';
import { purposeOfUse as purposeOfUseValue, useReasonDetailDisplayType, yesNo } from '../../../containers/common/constant/classification'
import { UseReasonDetailRadioGroup } from './useReasonDetailRadioGroup'
import { translate } from '../../../i18n'
import { getValueByNameStr } from '../../../utils/objectUtil'
import { GetUseReasonDto } from '../../../dataAccess/webApi/dto/useReasonDto'
import { GItem, GItemContainer } from '../common/grids';

export interface UseReasonOptions {
  irregular: boolean,
  emergency: boolean,
  refresh: boolean
}

interface UseReasonRadioGroupProps {
  useReason?: GetUseReasonDto
  useReasonOptions: UseReasonOptions
  facility?: {
    irregularDisplayType: string
    emergencyDisplayType: string
    refreshDisplayType: string
    
    useReasonUsageFlagIrregular: string
    useReasonDetailUsageFlagIrregular: string
    detailDisplayTypeIrregular: string
    orderIrregular: string
    detailsIrregular: {
      useReasonDetailCategory: string
      detailItemFlag: string
    }[]
    useReasonUsageFlagEmg: string
    useReasonDetailUsageFlagEmg: string
    detailDisplayTypeEmg: string
    orderEmg: string
    detailsEmg: {
      useReasonDetailCategory: string
      detailItemFlag: string
    }[]
    useReasonUsageFlagRefresh: string
    useReasonDetailUsageFlagRefresh: string
    detailDisplayTypeRefresh: string
    orderRefresh: string
    detailsRefresh: {
      useReasonDetailCategory: string
      detailItemFlag: string
    }[]
  },
  purposeOfUse: string
  useReasonDetailIrregular: string
  useReasonDetailEmg: string
  useReasonDetailRefresh: string
  onChange?: (mewPurpose: string) => void
  isDetailHidden?: boolean
  setValue: UseFormSetValue<any>
}

const StyledRadioGroup = styled(RadioGroup)(() => ({
  '& .MuiRadio-root': {
    padding: '0 9px',
  },
}))

export const UseReasonRadioGroup = (props: UseReasonRadioGroupProps) => {
  const {
    useReason,
    useReasonOptions,
    facility,
    purposeOfUse,
    useReasonDetailIrregular,
    useReasonDetailEmg,
    useReasonDetailRefresh,
    onChange,
    isDetailHidden: isDetailHiddenProps,
    setValue,
  } = props
  
  const {
    field: { ref, ...ctrlProps },
    formState: { errors },
  } = useController({
    name: 'purposeOfUse',
    rules: {
      required: {
        value: true,
        message: translate('system.error.requiredSelection', translate('facilityReservationForm.useReason.purpose')),
      },
    },
    defaultValue: ''
  })

  // 選択された利用目的以外に関連する詳細事由の選択を解除する
  useEffect(() => {
    if (purposeOfUse !== purposeOfUseValue.irregular) setValue('useReasonDetailIrregular', '')
    if (purposeOfUse !== purposeOfUseValue.emergency) setValue('useReasonDetailEmg', '')
    if (purposeOfUse !== purposeOfUseValue.refresh) setValue('useReasonDetailRefresh', '')
  }, [
    purposeOfUse,
    setValue,
  ])

  // 詳細事由選択時に、それに紐づく利用目的を選択する
  useEffect(() => {
    if (useReasonDetailIrregular) {
      onChange?.(purposeOfUseValue.irregular)
      setValue('purposeOfUse', purposeOfUseValue.irregular)
    }
  }, [
    useReasonDetailIrregular,
    setValue,
    onChange,
  ])
  useEffect(() => {
    if (useReasonDetailEmg) {
      onChange?.(purposeOfUseValue.emergency)
      setValue('purposeOfUse', purposeOfUseValue.emergency)
    }
  }, [
    useReasonDetailEmg,
    setValue,
    onChange,
  ])
  useEffect(() => {
    if (useReasonDetailRefresh) {
      onChange?.(purposeOfUseValue.refresh)
      setValue('purposeOfUse', purposeOfUseValue.refresh)
    }
  }, [
    useReasonDetailRefresh,
    setValue,
    onChange,
  ])

  const onChangeUseReason = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value)
  }, [onChange])
  
  const error = getValueByNameStr(errors, 'purposeOfUse')
    
  if (!useReason || !facility) {
    return null
  }

  const isDetailHidden = isDetailHiddenProps ?? false;

  const radioGroups = []

  if (useReasonOptions.irregular) {
    radioGroups.push(
      {
        order: useReason.orderIrregular,
        component: (ref?: RefCallBack) => (<RadioGroupByUseReason
          key='irregular'
          useReasonName='Irregular'
          useReasonDisplayName={useReason.displayNameForCitizenIrregular}
          purposeOfUse={purposeOfUse}
          useReasonDetail={useReasonDetailIrregular}
          onChange={onChangeUseReason}
          isDetailHidden={isDetailHidden}
          detailDisplayType={facility.detailDisplayTypeIrregular}
          useReasonCategoryValue={purposeOfUseValue.irregular}
          detailUsageFlag={useReason.detailUsageFlagIrregular}
          useReasonDetails={facility?.detailsIrregular ?? []}
          buttonValueLabels={useReason.detailsIrregular.map((detail) => ({ value: detail.category, label: detail.name, textInputType: detail.textInputType }))}
          ref={ref}
        />),
      },
    )
  }

  if (useReasonOptions.emergency) {
    radioGroups.push(
      {
        order: useReason.orderEmg,
        component: (ref?: RefCallBack) => (<RadioGroupByUseReason
          key='emergency'
          useReasonName='Emg'
          useReasonDisplayName={useReason.displayNameForCitizenEmg}
          purposeOfUse={purposeOfUse}
          useReasonDetail={useReasonDetailEmg}
          onChange={onChangeUseReason}
          isDetailHidden={isDetailHidden}
          detailDisplayType={facility.detailDisplayTypeEmg}
          useReasonCategoryValue={purposeOfUseValue.emergency}
          detailUsageFlag={useReason.detailUsageFlagEmg}
          useReasonDetails={facility?.detailsEmg ?? []}
          buttonValueLabels={useReason.detailsEmg.map((detail) => ({ value: detail.category, label: detail.name, textInputType: detail.textInputType }))}
          ref={ref}
        />),
      },
    )
  }

  if (useReasonOptions.refresh) {
    radioGroups.push(
      {
        order: useReason.orderRefresh,
        component: (ref?: RefCallBack) => (<RadioGroupByUseReason
          key='refresh'
          useReasonName='Refresh'
          useReasonDisplayName={useReason.displayNameForCitizenRefresh}
          purposeOfUse={purposeOfUse}
          useReasonDetail={useReasonDetailRefresh}
          onChange={onChangeUseReason}
          isDetailHidden={isDetailHidden}
          detailDisplayType={facility.detailDisplayTypeRefresh}
          useReasonCategoryValue={purposeOfUseValue.refresh}
          detailUsageFlag={useReason.detailUsageFlagRefresh}
          useReasonDetails={facility?.detailsRefresh ?? []}
          buttonValueLabels={useReason.detailsRefresh.map((detail) => ({ value: detail.category, label: detail.name, textInputType: detail.textInputType }))}
          ref={ref}
        />),
      },
    )
  }

  radioGroups.sort((a, b) => (a.order - b.order))

  return (
    <FormControl error={!!error} required>
      <StyledRadioGroup
        {...ctrlProps}
      >
        {radioGroups.map(({component}, index) => index === 0 ? component(ref) : component())}
      </StyledRadioGroup>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}

interface RadioGroupByUseReasonProps {
  /** 利用目的表示名 */
  useReasonDisplayName: string

  /** 利用目的名 */
  useReasonName: 'Irregular' | 'Emg' | 'Refresh'

  /** 
   * 詳細事由表示タイプ
   * '1': 利用目的と詳細事由を表示
   * '2': 詳細事由のみ表示
   */
  detailDisplayType: string

  /** 
   * このラジオボタンがどの利用目的に対応するものかを指定する
   * '1': 非定型
   * '2': 緊急
   * '3': リフレッシュ
   */
  useReasonCategoryValue: string

  /** 利用目的マスタで設定される詳細事由を利用するか否かを指定するフラグ */
  detailUsageFlag: string

  /** 各詳細事由を利用するか否かのフラグ */
  useReasonDetails: {
    useReasonDetailCategory: string
    detailItemFlag: string
  }[]

  /** 選択されている利用目的 */
  purposeOfUse: string
  /** 選択されている詳細事由 */
  useReasonDetail: string

  /** 未ログイン時に詳細事由を非表示とするのに使う */
  isDetailHidden?: boolean

  /** 詳細事由の選択肢の値と表示文言配列 */
  buttonValueLabels: { value: string; label: string, textInputType: string }[]

  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const RadioGroupByUseReason = forwardRef(
  function RadioGroupByUseReasonBase(props: RadioGroupByUseReasonProps, ref) {
    const { useReasonDisplayName, useReasonName, detailDisplayType, purposeOfUse, useReasonDetail, useReasonCategoryValue, detailUsageFlag, useReasonDetails, onChange, isDetailHidden: isDetailHiddenProps, buttonValueLabels } = props

    const isDetailHidden = isDetailHiddenProps ?? false;

    return (
      detailDisplayType === useReasonDetailDisplayType.useReasonAndDetails
      ? <GItemContainer direction='column'>
          <GItem>
            <FormControlLabel
              value={useReasonCategoryValue}
              control={<Radio onChange={onChange}/>}
              label={useReasonDisplayName}
              inputRef={ref}
            />
          </GItem>
          <GItemContainer>
            {
              detailUsageFlag === yesNo.yes
              && purposeOfUse === useReasonCategoryValue
              && !isDetailHidden
              && (
                <UseReasonDetailRadioGroup
                  useReasonName={useReasonName}
                  label={translate('facilityReservationForm.useReason.details')}
                  buttonValueLabels={buttonValueLabels}
                  required
                  useReasonDetail={useReasonDetail}
                  useReasonDetailFlags={useReasonDetails}
                  lowerIndentation
                />
              )
            }
          </GItemContainer>
        </GItemContainer> 
      : <GItemContainer wrap='wrap'>
          <UseReasonDetailRadioGroup
            useReasonName={useReasonName}
            label={translate('facilityReservationForm.useReason.details')}
            buttonValueLabels={buttonValueLabels}
            required={purposeOfUse === useReasonCategoryValue}
            useReasonDetail={useReasonDetail}
            useReasonDetailFlags={useReasonDetails}
            lowerIndentation={false}
          />
        </GItemContainer>
    )
  }
)